import { useEffect, useMemo, useState } from 'react'

import { useInView } from 'react-intersection-observer'
import { useInfiniteQuery } from 'react-query'
import BeatLoader from 'react-spinners/BeatLoader'

import UserCardContent from 'components/UI/UserCard/UserCardContent'
import {
  FeedAnalyticsCardContent,
  FeedAnalyticsCardDropdown
} from 'components-ref/organisms/analytics/content'
import { getContentMessagesAnalytics } from 'services/endpoints/analytic'
import { useAnalyticsContentCacheStore } from 'store/analyticsContentCacheStore'
import { type ContentFilter } from 'types/api/analytic'
import { type ContentModalSortFilter } from 'types/interfaces/IContentModalSortFilter'

interface MessagesAnalyticsProps {
  selectedCategory?: string
  filter?: ContentFilter
  sort: ContentModalSortFilter
}

const getMessageThumb = (card: any): string => {
  // Prefer real thumbs first
  const photo0 = card?.photos?.[0]
  if (photo0?.thumb_url) return photo0.thumb_url
  if (photo0?.url) return photo0.url

  const vPrev0 = card?.videos_preview?.[0]
  if (vPrev0?.active_thumb) return vPrev0.active_thumb
  if (vPrev0?.preview?.src) return vPrev0.preview.src

  // Sometimes videos may have active_thumb (not in your sample, but safe)
  const v0 = card?.videos?.[0]
  if (v0?.active_thumb) return v0.active_thumb

  return ''
}

export function MessagesAnalytics({
  selectedCategory,
  filter,
  sort
}: MessagesAnalyticsProps) {
  const { ref, inView } = useInView({})
  const { hydrated, getCache, setCache } = useAnalyticsContentCacheStore()

  const [ready, setReady] = useState(false)
  const [fallbackData, setFallbackData] = useState<any[] | null>(null)
  const [enabled, setEnabled] = useState(false)
  const normalizedCategoryId = selectedCategory || undefined

  const cacheKey = useMemo(() => {
    return `messages:${normalizedCategoryId ?? 'all'}:${JSON.stringify(filter)}:${JSON.stringify(sort)}`
  }, [normalizedCategoryId, filter, sort])

  const queryKey = useMemo(
    () => [
      'contentAnalytics',
      'messages',
      normalizedCategoryId ?? 'all',
      sort,
      JSON.stringify(filter)
    ],
    [normalizedCategoryId, sort, filter]
  )
  // Hydrate from Zustand cache (same as FeedAnalytics)
  useEffect(() => {
    if (!hydrated) return
    const cached = getCache(cacheKey)
    if (cached?.data?.length) {
      setFallbackData(cached.data)
    }
    setEnabled(true)
    setReady(true)
  }, [hydrated, getCache, cacheKey])

  const {
    data,
    isLoading,
    fetchNextPage,
    isFetching,
    hasNextPage
  } = useInfiniteQuery<any>(
    queryKey,
    async ({ pageParam = 1 }) => {
      return await getContentMessagesAnalytics({
        categoryId: normalizedCategoryId,
        filter,
        pageParam,
        sort
      })
    },
    {
      // If your endpoint returns Laravel meta/links like your sample:
      getNextPageParam: (lastPage: any) => {
        const current = Number(lastPage?.meta?.current_page ?? 1)
        const last = Number(lastPage?.meta?.last_page ?? 1)
        return current < last ? current + 1 : undefined
      },

      enabled,
      // staleTime: 60_000,
      keepPreviousData: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false
    }
  )

  // Save fresh data to Zustand cache (same as FeedAnalytics)
  useEffect(() => {
    if (data && !isFetching) {
      const all =
        data?.pages?.flatMap?.((p: any) => (Array.isArray(p?.data) ? p.data : [])) ?? []

      setCache(cacheKey, {
        data: all,
        lastFetched: Date.now()
      })
      setFallbackData(all)
    }
  }, [data, isFetching, cacheKey, setCache])

  // Pagination observer
  useEffect(() => {
    if (inView && hasNextPage && !isFetching && !isLoading) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetching, isLoading, fetchNextPage])

  if (!ready) return null

  const hasCached = !!fallbackData?.length
  const isFirstLoadWithoutCache = !hasCached && isLoading

  if (isFirstLoadWithoutCache) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
        <BeatLoader size={8} />
      </div>
    )
  }

  const allCards =
    data?.pages?.flatMap?.((p: any) => (Array.isArray(p?.data) ? p.data : [])) ??
    fallbackData ??
    []

  return (
    <>
      {allCards.map((card: any, index: number) => (
        <span
          ref={hasNextPage && index === allCards.length - 1 ? ref : null}
          key={card?.id ?? index}
        >
      <UserCardContent
        thumb_src={getMessageThumb(card)}
        cardContent={<FeedAnalyticsCardContent card={card} sort={sort} index={index} />}
        hasDropDown={true}
        dropDownContent={<FeedAnalyticsCardDropdown card={card} />}
        card={card}
        customIconClass='content-icon-square'
        iconSize='80'
        previewType="message"
      />
        </span>
      ))}
    </>
  )
}